/**
 * Template Service
 * Fetches AI-generated templates from database
 * Falls back to hardcoded logic if templates not found
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Fetch trait template from database
 * @param {string} traitType - 'brain', 'eye', 'ear', 'hand', 'foot'
 * @param {string} dominanceSide - 'left', 'right'
 * @returns {Promise<Object|null>} Template data or null
 */
export async function fetchTraitTemplate(traitType, dominanceSide) {
  try {
    const response = await fetch(`${API_BASE}/api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'template.getTrait',
        data: { traitType, dominanceSide }
      })
    });

    if (!response.ok) return null;
    
    const result = await response.json();
    return result.success ? result.template : null;
  } catch (error) {
    console.warn(`Template fetch failed for ${traitType}-${dominanceSide}, using fallback`, error);
    return null;
  }
}

/**
 * Fetch blueprint template from database
 * @param {string} blueprintType - 'LLL', 'RRR', etc.
 * @returns {Promise<Object|null>} Template data or null
 */
export async function fetchBlueprintTemplate(blueprintType) {
  try {
    const response = await fetch(`${API_BASE}/api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'template.getBlueprint',
        data: { blueprintType }
      })
    });

    if (!response.ok) return null;
    
    const result = await response.json();
    return result.success ? result.template : null;
  } catch (error) {
    console.warn(`Template fetch failed for blueprint ${blueprintType}, using fallback`, error);
    return null;
  }
}

/**
 * Fetch seating template from database
 * @param {string} brainDominance - 'left', 'right'
 * @returns {Promise<Object|null>} Template data or null
 */
export async function fetchSeatingTemplate(brainDominance) {
  try {
    const response = await fetch(`${API_BASE}/api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'template.getSeating',
        data: { brainDominance }
      })
    });

    if (!response.ok) return null;
    
    const result = await response.json();
    return result.success ? result.template : null;
  } catch (error) {
    console.warn(`Template fetch failed for seating ${brainDominance}, using fallback`, error);
    return null;
  }
}

/**
 * Fetch recommendations template from database
 * @param {string} brainDominance - 'left', 'right'
 * @param {string} learningStyle - 'visual', 'auditory', etc.
 * @param {string} focusArea - 'academic', 'athletic', etc.
 * @returns {Promise<Object|null>} Template data or null
 */
export async function fetchRecommendationsTemplate(brainDominance, learningStyle, focusArea = 'academic') {
  try {
    const response = await fetch(`${API_BASE}/api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'template.getRecommendations',
        data: { brainDominance, learningStyle, focusArea }
      })
    });

    if (!response.ok) return null;
    
    const result = await response.json();
    return result.success ? result.template : null;
  } catch (error) {
    console.warn(`Template fetch failed for recommendations, using fallback`, error);
    return null;
  }
}

/**
 * Fetch career guidance template from database
 * @param {string} socialStyle - 'introvert', 'extravert', 'ambivert'
 * @param {string} learningStyle - 'visual', 'auditory', etc.
 * @param {string} brainDominance - 'left', 'right'
 * @param {string} sportCategory - 'team', 'individual', etc.
 * @returns {Promise<Object|null>} Template data or null
 */
export async function fetchCareerGuidanceTemplate(socialStyle, learningStyle, brainDominance, sportCategory = 'general') {
  try {
    const response = await fetch(`${API_BASE}/api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'template.getCareerGuidance',
        data: { socialStyle, learningStyle, brainDominance, sportCategory }
      })
    });

    if (!response.ok) return null;
    
    const result = await response.json();
    return result.success ? result.template : null;
  } catch (error) {
    console.warn(`Template fetch failed for career guidance, using fallback`, error);
    return null;
  }
}

/**
 * Fetch next steps template from database
 * @param {string} brainDominance - 'left', 'right'
 * @param {string} temperament - 'analytical', 'creative'
 * @param {string} blueprintType - 'LLL', 'RRR', etc.
 * @returns {Promise<Object|null>} Template data or null
 */
export async function fetchNextStepsTemplate(brainDominance, temperament, blueprintType) {
  try {
    const response = await fetch(`${API_BASE}/api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'template.getNextSteps',
        data: { brainDominance, temperament, blueprintType }
      })
    });

    if (!response.ok) return null;
    
    const result = await response.json();
    return result.success ? result.template : null;
  } catch (error) {
    console.warn(`Template fetch failed for next steps, using fallback`, error);
    return null;
  }
}

/**
 * Personalize template text with user data
 * @param {string} text - Template text with placeholders
 * @param {Object} personalInfo - User's personal information
 * @returns {string} Personalized text
 */
export function personalizeTemplate(text, personalInfo) {
  if (!text) return text;
  
  return text
    .replace(/\{name\}/g, personalInfo.firstName || 'Student')
    .replace(/\{firstName\}/g, personalInfo.firstName || 'Student')
    .replace(/\{lastName\}/g, personalInfo.lastName || '')
    .replace(/\{sport\}/g, personalInfo.sport || 'Athletics')
    .replace(/\{school\}/g, personalInfo.schoolName || 'School')
    .replace(/\{grade\}/g, personalInfo.grade || '10');
}
